import { useState } from 'react';
import { Button, makeStyles } from '@fluentui/react-components';
import { DEMO_CHAT_SAMPLES } from '../demo/demoCoachContent.js';
import { publicDemoProductUrl } from '../lib/publicDemo.js';
import { useAuthStore } from '../stores/authStore.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '720px',
    margin: '0 auto',
    padding: '8px 0 24px',
  },
  note: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.45,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  thread: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    maxWidth: '85%',
    background: 'rgba(12, 111, 214, 0.12)',
    border: '1px solid rgba(12, 111, 214, 0.25)',
    borderRadius: '12px 12px 4px 12px',
    padding: '10px 12px',
    fontSize: 'var(--fs-200)',
  },
  bubbleBot: {
    alignSelf: 'flex-start',
    maxWidth: '90%',
    background: 'var(--bg-page)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '12px 12px 12px 4px',
    padding: '10px 12px',
    fontSize: 'var(--fs-200)',
    lineHeight: 1.45,
  },
  tag: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--opus-brand-60, #0c6fd6)',
    marginBottom: '6px',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    marginTop: '4px',
  },
});

type Msg = { role: 'user' | 'assistant'; text: string };

/**
 * Chat de muestra del proyecto ejemplo: 3 preguntas + respuestas fijas.
 * No llama a la API de chat (cero tokens).
 */
export function DemoSampleChat() {
  const styles = useStyles();
  const productUrl = useAuthStore((s) => s.demoProductUrl) || publicDemoProductUrl();
  const [msgs, setMsgs] = useState<Msg[]>([
    {
      role: 'assistant',
      text:
        'Puedes probar estas preguntas sobre el concurso de muestra. Las respuestas son de demostración: no consumen créditos de inteligencia artificial.',
    },
  ]);

  function ask(q: string, a: string) {
    setMsgs((prev) => [
      ...prev,
      { role: 'user', text: q },
      { role: 'assistant', text: a },
    ]);
  }

  return (
    <div className={styles.root}>
      <p className={styles.note}>
        Chat de demostración con datos de muestra de Ecosoft. En tus propias obras el asistente
        responde desde tus documentos indexados.
      </p>
      <div className={styles.chips}>
        {DEMO_CHAT_SAMPLES.map((s) => (
          <Button
            key={s.question}
            appearance="secondary"
            size="small"
            onClick={() => ask(s.question, s.answer)}
          >
            {s.question}
          </Button>
        ))}
      </div>
      <div className={styles.thread} aria-live="polite">
        {msgs.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className={styles.bubbleUser}>
              {m.text}
            </div>
          ) : (
            <div key={i} className={styles.bubbleBot}>
              <div className={styles.tag}>Respuesta demo</div>
              {m.text}
            </div>
          ),
        )}
      </div>
      <div className={styles.footer}>
        <Button
          appearance="primary"
          onClick={() => window.open(productUrl, '_blank', 'noopener,noreferrer')}
        >
          Activar Start
        </Button>
      </div>
    </div>
  );
}
